import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import CloseRounded from '@mui/icons-material/CloseRounded';
import CheckCircleOutlineRounded from '@mui/icons-material/CheckCircleOutlineRounded';
import ForumRounded from '@mui/icons-material/ForumRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import PhoneRounded from '@mui/icons-material/PhoneRounded';
import ReplayRounded from '@mui/icons-material/ReplayRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams, useSearchParams } from 'react-router';
import { EmptyState, ErrorState, LoadingRows, PageHeader } from '../../components/states';
import { StatusChip } from '../../components/StatusChip';
import { Segmented } from '../../components/ui';
import { dateTime, plural, relativeTime } from '../../lib/format';
import { errorMessage, useNotify } from '../../lib/notify';
import { STATUS_LABELS, type DisplayStatus } from '../tenants/api';
import { TenantAvatar } from '../tenants/parts';
import { MessageButtons } from '../messages/MessageButtons';
import {
  fetchOlder,
  useInbox,
  useMarkRead,
  useReply,
  useSetStatus,
  useThread,
  type Conversation,
  type Message,
} from './api';

type StatusFilter = 'OPEN' | 'RESOLVED' | 'ALL';
const MAX_MESSAGE = 4000;

function stateChip(c: Conversation) {
  const s = (
    c.tenant.status === 'ACTIVE' ? c.tenant.subscriptionState : c.tenant.status
  ) as DisplayStatus | null;
  return s ? <StatusChip status={s} label={STATUS_LABELS[s]} /> : null;
}

function ConversationRow({ c, selected }: { c: Conversation; selected: boolean }) {
  const [params] = useSearchParams();
  const search = params.toString();
  return (
    <Box
      component={RouterLink}
      to={`/inbox/${c.id}${search ? `?${search}` : ''}`}
      aria-current={selected ? 'true' : undefined}
      sx={{
        display: 'flex',
        gap: 1.5,
        px: 2,
        py: 1.5,
        color: 'inherit',
        textDecoration: 'none',
        borderBottom: 1,
        borderColor: 'divider',
        bgcolor: selected ? 'action.selected' : undefined,
        '&:hover': { bgcolor: selected ? 'action.selected' : 'action.hover' },
      }}
    >
      <Badge color="primary" badgeContent={c.unread} max={99} overlap="circular">
        <TenantAvatar name={c.tenant.name} size={38} />
      </Badge>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
          <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: c.unread > 0 ? 700 : 600 }}>
            {c.tenant.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            {relativeTime(c.lastMessageAt)}
          </Typography>
        </Stack>
        <Typography
          variant="body2"
          color={c.unread > 0 ? 'text.primary' : 'text.secondary'}
          noWrap
          sx={{ mt: 0.25 }}
        >
          {c.lastSender === 'PLATFORM' ? 'You: ' : ''}
          {c.lastMessagePreview}
        </Typography>
        <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, alignItems: 'center' }}>
          <Typography variant="caption" color="text.secondary" noWrap>
            {c.tenant.slug}
          </Typography>
          {stateChip(c)}
          {c.status === 'RESOLVED' && <StatusChip status="COMPLETED" label="Resolved" />}
        </Stack>
      </Box>
    </Box>
  );
}

function Bubble({ m }: { m: Message }) {
  const mine = m.sender === 'PLATFORM';
  const contact = [m.contactEmail, m.contactPhone].filter(Boolean).join(' · ');
  return (
    <Box sx={{ alignSelf: mine ? 'flex-end' : 'flex-start', maxWidth: { xs: '88%', sm: '75%' } }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mb: 0.25, textAlign: mine ? 'right' : 'left' }}
      >
        {m.author.name} · {dateTime(m.createdAt)}
      </Typography>
      <Box
        sx={(t) => ({
          px: 1.75,
          py: 1.25,
          borderRadius: 3,
          borderTopRightRadius: mine ? 6 : undefined,
          borderTopLeftRadius: mine ? undefined : 6,
          whiteSpace: 'pre-wrap',
          overflowWrap: 'anywhere',
          bgcolor: mine ? 'primary.main' : 'background.paper',
          color: mine ? 'primary.contrastText' : 'text.primary',
          border: mine ? 'none' : `1px solid ${(t.vars ?? t).palette.divider}`,
        })}
      >
        {m.body}
      </Box>
      {!mine && contact && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
          Reach me at {contact}
        </Typography>
      )}
    </Box>
  );
}

function ThreadView({
  id,
  wide,
  onClose,
}: {
  id: string;
  wide: boolean;
  /** Back to the inbox list (also Esc). */
  onClose: () => void;
}) {
  const thread = useThread(id);
  const reply = useReply(id);
  const setStatus = useSetStatus(id);
  const { mutate: markRead } = useMarkRead(id);
  const notify = useNotify();
  const [body, setBody] = useState('');
  const [older, setOlder] = useState<Message[]>([]);
  const [olderMore, setOlderMore] = useState<boolean | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);

  const conversation = thread.data?.conversation;
  const messages = useMemo(
    () => [...older, ...(thread.data?.messages ?? [])],
    [older, thread.data?.messages],
  );
  const hasMore = olderMore ?? thread.data?.hasMore ?? false;

  const unread = conversation?.unread ?? 0;
  useEffect(() => {
    if (unread > 0) markRead();
  }, [unread, markRead]);

  const lastId = messages.at(-1)?.id;
  useEffect(() => {
    const log = logRef.current;
    if (log) log.scrollTop = log.scrollHeight;
  }, [lastId]);

  if (thread.isPending) return <LoadingRows rows={5} />;
  if (thread.isError || !conversation)
    return <ErrorState error={thread.error} onRetry={() => void thread.refetch()} />;

  const canSend = body.trim().length > 0 && body.length <= MAX_MESSAGE;
  const submit = () => {
    if (!canSend || reply.isPending) return;
    reply.mutate({ id: crypto.randomUUID(), body: body.trim() }, { onSuccess: () => setBody('') });
  };
  const loadOlder = async () => {
    const first = messages[0];
    if (!first) return;
    setLoadingOlder(true);
    try {
      const page = await fetchOlder(id, first.createdAt);
      setOlder((prev) => [...page.messages, ...prev]);
      setOlderMore(page.hasMore);
    } catch (e) {
      notify.error(e);
    } finally {
      setLoadingOlder(false);
    }
  };
  const resolved = conversation.status === 'RESOLVED';

  return (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}
      >
        {!wide && (
          <IconButton aria-label="Back to inbox" onClick={onClose} edge="start">
            <ArrowBackRounded />
          </IconButton>
        )}
        <TenantAvatar name={conversation.tenant.name} size={38} />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
            <Link
              component={RouterLink}
              to={`/tenants/${conversation.tenant.id}`}
              underline="hover"
              noWrap
              sx={{ fontWeight: 650 }}
            >
              {conversation.tenant.name}
            </Link>
            {stateChip(conversation)}
          </Stack>
          <Stack
            direction="row"
            spacing={1.5}
            sx={{ mt: 0.25, flexWrap: 'wrap', rowGap: 0.25, color: 'text.secondary' }}
          >
            <Typography variant="caption">{conversation.tenant.slug}</Typography>
            {conversation.contactEmail && (
              <Typography variant="caption" sx={{ display: 'inline-flex', gap: 0.5 }}>
                <MailOutlineRounded sx={{ fontSize: 14 }} />
                <Link href={`mailto:${conversation.contactEmail}`} color="inherit">
                  {conversation.contactEmail}
                </Link>
              </Typography>
            )}
            {conversation.contactPhone && (
              <Typography variant="caption" sx={{ display: 'inline-flex', gap: 0.5 }}>
                <PhoneRounded sx={{ fontSize: 14 }} />
                <Link href={`tel:${conversation.contactPhone}`} color="inherit">
                  {conversation.contactPhone}
                </Link>
              </Typography>
            )}
          </Stack>
        </Box>
        <MessageButtons
          template="inbox"
          compact
          tenant={conversation.tenant}
          prefer={{ phone: conversation.contactPhone, email: conversation.contactEmail }}
        />
        <Button
          size="small"
          variant={resolved ? 'outlined' : 'contained'}
          color={resolved ? 'inherit' : 'success'}
          startIcon={resolved ? <ReplayRounded /> : <CheckCircleOutlineRounded />}
          disabled={setStatus.isPending}
          onClick={() =>
            setStatus.mutate(resolved ? 'OPEN' : 'RESOLVED', {
              onSuccess: () => notify.success(resolved ? 'Reopened' : 'Marked as resolved'),
              onError: (e) => notify.error(e),
            })
          }
          sx={{ flexShrink: 0 }}
        >
          {resolved ? 'Reopen' : 'Resolve'}
        </Button>
        {wide && (
          <Tooltip title="Close (Esc)">
            <IconButton aria-label="Close conversation" onClick={onClose} edge="end">
              <CloseRounded />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Box
        ref={logRef}
        role="log"
        aria-label={`Messages with ${conversation.tenant.name}`}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          p: 2,
          bgcolor: 'action.hover',
        }}
      >
        {hasMore && (
          <Button
            size="small"
            onClick={() => void loadOlder()}
            disabled={loadingOlder}
            sx={{ alignSelf: 'center' }}
          >
            {loadingOlder ? 'Loading…' : 'Show earlier messages'}
          </Button>
        )}
        {messages.map((m) => (
          <Bubble key={m.id} m={m} />
        ))}
        {resolved && conversation.resolvedAt && (
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
            Resolved {relativeTime(conversation.resolvedAt)}. A new message from the shop reopens
            it.
          </Typography>
        )}
      </Box>

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-end' }}>
          <TextField
            label="Reply"
            placeholder="Write a reply… (the shop sees it from PackBytes Support)"
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            value={body}
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                submit();
              }
            }}
            error={body.length > MAX_MESSAGE}
            helperText={
              body.length > MAX_MESSAGE
                ? `At most ${String(MAX_MESSAGE)} characters.`
                : 'Ctrl + Enter to send'
            }
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!canSend || reply.isPending}
            endIcon={
              reply.isPending ? <CircularProgress size={16} color="inherit" /> : <SendRounded />
            }
            sx={{ mb: 3, flexShrink: 0 }}
          >
            Send
          </Button>
        </Stack>
        {reply.isError && <Alert severity="error">{errorMessage(reply.error)}</Alert>}
      </Box>
    </Stack>
  );
}

/** Every shop's chat with PackBytes support: the inbox on the left, the open thread on the right. */
export function SupportInboxPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const wide = useMediaQuery(theme.breakpoints.up('md'));
  const [params, setParams] = useSearchParams();
  const status = (params.get('status') ?? 'OPEN') as StatusFilter;
  const unreadOnly = params.get('unread') === '1';
  const [q, setQ] = useState(params.get('q') ?? '');

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value === null) next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };
  // Search as the admin types, a moment after the last key.
  useEffect(() => {
    const t = setTimeout(() => {
      if ((params.get('q') ?? '') !== q.trim()) setParam('q', q.trim() || null);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const inbox = useInbox({
    page: 1,
    pageSize: 100,
    ...(status === 'ALL' ? {} : { status }),
    ...(unreadOnly ? { unread: 'true' as const } : {}),
    ...(params.get('q') ? { q: params.get('q') ?? '' } : {}),
  });
  const rows = inbox.data?.data;
  const back = () => void navigate(`/inbox${params.toString() ? `?${params.toString()}` : ''}`);
  // Esc closes the open conversation (unless a menu, dialog or field already handled it).
  useEffect(() => {
    if (!id) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      void navigate(`/inbox${params.toString() ? `?${params.toString()}` : ''}`);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [id, navigate, params]);

  const list = (
    <Stack sx={{ height: '100%', minHeight: 0 }}>
      <Stack spacing={1.25} sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <TextField
          size="small"
          placeholder="Search shop name or slug"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRounded fontSize="small" />
                </InputAdornment>
              ),
            },
            htmlInput: { 'aria-label': 'Search conversations' },
          }}
        />
        <Stack direction="row" sx={{ alignItems: 'center', justifyContent: 'space-between' }}>
          <Segmented<StatusFilter>
            label="Conversation status"
            value={status}
            onChange={(v) => setParam('status', v === 'OPEN' ? null : v)}
            options={[
              { value: 'OPEN', label: 'Open' },
              { value: 'RESOLVED', label: 'Resolved' },
              { value: 'ALL', label: 'All' },
            ]}
          />
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={unreadOnly}
                onChange={(e) => setParam('unread', e.target.checked ? '1' : null)}
              />
            }
            label={<Typography variant="body2">Unread</Typography>}
            sx={{ mr: 0 }}
          />
        </Stack>
      </Stack>
      <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {inbox.isError ? (
          <ErrorState error={inbox.error} onRetry={() => void inbox.refetch()} />
        ) : !rows ? (
          <LoadingRows rows={5} height={64} />
        ) : rows.length === 0 ? (
          <EmptyState
            dense
            icon={<ForumRounded />}
            title={unreadOnly ? 'Nothing waiting for a reply' : 'No conversations'}
            body="When a shop sends a message from its Support page or locked screen, it shows up here."
          />
        ) : (
          rows.map((c) => <ConversationRow key={c.id} c={c} selected={c.id === id} />)
        )}
      </Box>
      {inbox.data && (
        <Typography variant="caption" color="text.secondary" sx={{ px: 2, py: 1 }}>
          {plural(inbox.data.page.total, 'conversation')}
        </Typography>
      )}
    </Stack>
  );

  const thread = id ? (
    <ThreadView key={id} id={id} wide={wide} onClose={back} />
  ) : (
    <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
      <EmptyState
        dense
        icon={<ForumRounded />}
        title="Pick a conversation"
        body="Choose a shop on the left to read its messages and reply."
      />
    </Stack>
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Customers"
        title="Support inbox"
        subtitle="Messages from shops, including locked ones. Replies reach the shop signed “PackBytes Support”."
      />
      <Card
        sx={{
          display: 'grid',
          gridTemplateColumns: wide ? 'minmax(300px, 380px) 1fr' : '1fr',
          height: { xs: 'calc(100dvh - 220px)', md: 'calc(100dvh - 240px)' },
          minHeight: 480,
          overflow: 'hidden',
        }}
      >
        {(wide || !id) && (
          <Box sx={{ minHeight: 0, borderRight: wide ? 1 : 0, borderColor: 'divider' }}>{list}</Box>
        )}
        {(wide || id) && <Box sx={{ minHeight: 0 }}>{thread}</Box>}
      </Card>
    </Box>
  );
}
